import React, { FC, MouseEvent } from 'react';
import { Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';

import { PaymentDetails, Terminal } from '~/types';

import { usePath, usePermissions } from '~/hooks';
import { usePageShow } from '~/hooks/usePageQuery';
import { paymentDetails, terminals } from '~/routes';
import { Longdash, statusCell } from '~/utils';
import { AmountField } from '~/components';

interface Props {
  data: PaymentDetails[] | undefined;
  loading: boolean;
  total: any;
  terminalsById: Record<string, Terminal>;
}

const PaymentDetailsTable: FC<Props> = (props) => {
  const { data, loading, total, terminalsById } = props;
  const { pagination } = usePath();
  const { t } = useTranslation('paymentDetails');

  const { showOpen } = usePageShow(paymentDetails);
  const { showOpen: terminalShowOpen } = usePageShow(terminals);

  const handleTerminalOpen = (terminalId?: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (!terminalId) return;
    e.stopPropagation();
    terminalShowOpen(terminalId);
  };

  const columns: ColumnsType<PaymentDetails> = [
    statusCell((record) => {
      if (record.terminalId) return 'ACTIVE';
      if (!record.terminalId) return 'CLOSED';
      return '';
    }),
    {
      title: t('name'),
      dataIndex: 'name',
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      // align: 'right',
      render: (_, record) => {
        return <AmountField amount={record.amount} />;
      },
    },
    {
      title: t('terminal'),
      dataIndex: 'terminalId',
      render: (_, record) => {
        const id = record.terminalId;
        if (!id) return <div>{Longdash}</div>;
        return (
          <Typography.Link onClick={handleTerminalOpen(id)}>
            {terminalsById[id]?.label || Longdash}
          </Typography.Link>
        );
      },
    },
  ];

  const hasAccessView = usePermissions('business.paymentDetails.view');

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      rowKey={(record) => record?.id as string}
      pagination={{ ...pagination, total }}
      rowClassName="cursor-pointer"
      onRow={(record) => ({
        onClick: () => hasAccessView && showOpen(record.id as string),
      })}
    />
  );
};

export default PaymentDetailsTable;
